import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  selectEmailManagerFilterStatus,
  setEmailManagerFilterStatus,
  PendingArtifactStatusEnum,
  FileInStatusEnum,
} from "@/features/connections/integrationSlice";
import {
  ShepDataGridPro,
  ShepTooltip,
  ShepSearchBar,
  ShepButton,
  ShepCheckbox,
  ShepPagination,
  ShepHorizontalMenu,
} from "@/components/GenericComponents/ShepComponents";
import { selectIntegrationDateFilter } from "@/features/dateFilterSlice";
import "@/components/Integrations/FileManager/FileManagerContent.css";
import axios from "@/features/axiosHelper";
import { FilterDropDownType } from "@/utils/utils";
import CustomFilterDropdown from "@/components/CustomFilterDropdown/CustomFilterDropdown";
import { createAlert } from "@/features/homepage/alertSliceList";

import _ from "lodash";
import dayjs from "dayjs";
import { InfoIcon, EditIcon, CheckIcon, ResultError } from "@/ImageRepo";
import PendingArtifactEditor from "./PendingArtifactEditor";

const defaultFileInProperties = {
  FileInErrors: [],
  LastUpdated: null,
}

const EmailManagerContent = (props) => {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const statusFilter = useSelector(selectEmailManagerFilterStatus);
  var { startDate, endDate } = useSelector(selectIntegrationDateFilter) || {};
  const [onMount, setOnMount] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(false);

  const [rows, setRows] = useState([]);
  const [fileLoaded, setIsFileLoaded] = useState(false);
  const [currentPendingArtifact, setCurrentPendingArtifact] = useState({});
  const [fileDataText, setFileDataText] = useState("");
  const [artifactViewerOpen, setArtifactViewerOpen] = useState(false);
  // const [showUpload, setShowUpload] = useState(false);

  const gridRef = useGridApiRef();

  useEffect(() => {
    setFileDataText(
      currentPendingArtifact.JSONProperties?.EmailInformation?.EmailBody,
    );
  }, [currentPendingArtifact]);
  // Track selected row, update happens only on row click, not checkbox selection, only one selected row each time
  const [fileDetails, setFileDetails] = useState({
    ConnectionId: "",
    ConnectionName: "",
    FileId: "",
    FileName: "",
    Reference: "",
    Status: "",
    FileSize: "",
    FileDataText: "",
    FileData: "",
    CreateDate: "",
    FileInJSONProperties: defaultFileInProperties,
  });

  const [selectionModel, setSelectionModel] = useState([]); // array to keep track of all of the checkboxes selected

  const dataGridStyle = {
    color: "var(--text-primary)",
    fontSize: "var(--font-sm)",
    fontFamily: "var(--font-family)",
    width: "600px",
    overflowY: "hidden",
    overflowX: "hidden",
    "&, [class^=MuiDataGrid]": { border: "none" },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
      display: "none", // column headers not needed to be displayed
    },

    // Display item tool action bar when row is hovered, else shows date
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "var(--tertiary-13)",
    },
    "& .MuiDataGrid-row:hover .item-tools": {
      display: "inline",
    },
    "& .MuiDataGrid-row:hover .receive-date": {
      display: "none",
    },
    "& .MuiDataGrid-row .item-tools": {
      display: "none",
    },
    "& .MuiDataGrid-row .receive-date": {
      display: "inline",
    },

    // Row and checkbox selection
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
      {
        outline: "none",
      },
    "& .Mui-selected, & .MuiDataGrid-row.Mui-selected  ": {
      backgroundColor: "var(--tertiary-13)",
    },
    "& .MuiDataGrid-cellCheckbox": {
      borderLeft: "8px solid transparent",
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
    "& .Mui-selected .MuiDataGrid-cellCheckbox": {
      borderLeft: "8px solid var(--primary-main)",
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
  };

  const schema = [
    {
      id: 0,
      label: "Status",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Status",
      setValue: setEmailManagerFilterStatus,
      readVal: selectEmailManagerFilterStatus,
      labelKey: "Label",
      uniqueKey: "Value",
    },
  ];

  // Data grid containing file lists and details (Connection name, file name, status, file size, date)
  const columns = [
    {
      field: "fileDetails",
      hide: true,
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Box
              direction="row"
              sx={{
                fontSize: "16px",
                marginTop: "15px",
                marginBottom: "15px",
                width: "100%",
              }}
            >
              <Box
                className="list-header"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                Connection name: {params.row.ConnectionName ?? "Unknown"}
                <Box
                  className="email-status-icon"
                  sx={{
                    height: "20px",
                    width: "20px",
                    marginLeft: "auto",
                    marginRight: "15px",
                  }}
                >
                  {getStatusIcon(params.row.PendingArtifactStatus)}
                </Box>
              </Box>
              <Box className="list-header">
                File name: {params.row.FileName ?? "Unknown"}
              </Box>
              <Stack direction="row">
                <Box>
                  <Box display="flex" flexDirection="row">
                    Status:{" "}
                    {getStatusName(
                      params.row.PendingArtifactStatus,
                      params.row.FileInStatus,
                    ) ?? "Unknown"}
                  </Box>
                  <Box display="flex" flexDirection="row">
                    Confidence:{" "}
                    {(params.row.PendingArtifactProperties.Confidence ?? 0) *
                      100}
                    %
                  </Box>
                </Box>

                <Stack direction="row" marginLeft="auto">
                  <Stack className="item-tools" justifyContent="flex-end">
                    <ShepTooltip title="Edit Result">
                      <IconButton onClick={() => setArtifactViewerOpen(true)}>
                        <EditIcon />
                      </IconButton>
                    </ShepTooltip>
                    <ShepButton
                      size="small"
                      variant="secondary"
                      sx={{ height: 25, width: 30 }}
                      onClick={() =>
                        retranslateFile([params.row.PendingArtifactId], false)
                      }
                    >
                      Retranslate
                    </ShepButton>
                  </Stack>
                  <Box className="receive-date" style={{ paddingTop: 15 }}>
                    Date Received:{" "}
                    {convertDate(params.row.ReceivedDate) ?? "Unknown"}
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </>
        );
      },
    },
  ];

  const getStatusIcon = (statusNum) => {
    if (statusNum === PendingArtifactStatusEnum.AwaitingConfirmation)
      return <InfoIcon style={{ width: "100%", height: "100%" }} />;
    if (
      statusNum === PendingArtifactStatusEnum.Completed ||
      statusNum === PendingArtifactStatusEnum.Confirmed
    )
      return <CheckIcon style={{ width: "100%", height: "100%" }} />;
    if (
      statusNum === PendingArtifactStatusEnum.Rejected ||
      statusNum === PendingArtifactStatusEnum.ErrorInTranslation
    )
      return <ResultError style={{ width: "100%", height: "100%" }} />;
  };

  /**
   * Fetch status filter options depending on if it is on inbound or outbound tab
   */
  const getStatusTypes = () => {
    axios
      .get("/email/email-status")
      .then((response) => {
        dispatch(
          setEmailManagerFilterStatus({
            value: response.data,
            heapIOIsInitValue: true,
          }),
        );
      })
      .catch((error) => {
        console.error("Error fetching inbound status: ", error);
      });
  };

  /**
   * Fetch and set selected row's raw file data text
   * @param {string, bool} params: fileId, isOutbound
   */
  const getEmailData = (params) => {
    if (params.FileId !== "") {
      axios
        .get("/email/data", {
          params: params,
        })
        .then((response) => {
          if (response != null) {
            setCurrentPendingArtifact(response.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching raw file data: ", error);
        });
    }
  };

  /**
   * Reformat the received date
   * @param {string} dateStr
   */
  const convertDate = (dateStr) => {
    const utcDateStr = dateStr + "Z";
    const date = new Date(utcDateStr);

    const formattedDate = date.toLocaleString({
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });

    return formattedDate;
  };

  /**
   * Set the file details and raw file data based on row selected
   * @param selectedRow
   */
  const handleRowSelection = (selectedRow) => {
    if (selectedRow) {
      setFileDetails({
        ...selectedRow,
        FileDataText: fileDataText,
      });
    }
  };

  /**
   * Fetch inbound files list and set row, file details
   * @param params: startDate, endDate, connectionId, statusFilterJson, search
   */
  const getFilesFromServer = (params) => {
    axios
      .get("/email/inbound-files", {
        params: params,
      })
      .then((response) => {
        setFileDetails(response.data);
        setRows(response.data);
        selectFirstFile(response.data);
        setIsFileLoaded(true);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  // Auto-select and display the first file's details if available
  const selectFirstFile = (responseData) => {
    // ensure that there are files available
    const currentRows = responseData && responseData.length ? responseData : [];
    if (currentRows && currentRows.length) {
      const firstRow = { ...currentRows[0] };
      if (props.isOutbound) {
        firstRow.FileInJSONProperties = defaultFileInProperties;
      }
      setFileDetails(firstRow);
    }
  };

  const getFiles = (resetCache = false) => {
    if (!startDate || !endDate) return; //block records gathering until datefilter ready
    if (!onMount || fileLoaded) {
      setOnMount(true);
      // Apply date filter;
      const defaultStartDays = 7;

      if (startDate === "" && endDate === "") {
        startDate = dayjs().subtract(defaultStartDays, "day").startOf("day");
        endDate = dayjs().add(1, "day").startOf("day");
      } else if (endDate === null) {
        endDate = startDate;
      }

      const params = prepareGetProperties({
        resetCache: resetCache,
        startDate: startDate,
        endDate: endDate,
        statusFilterJson: statusFilter,
      });

      if (resetCache) {
        setIsFileLoaded(false);
      }
      selectFileType(params);
    }
  };

  const prepareGetProperties = (args) => {
    const params = {
      startDate: args.startDate ?? startDate,
      endDate: args.endDate ?? endDate,
      resetCache: args.resetCache ?? false,
      search: args.search ?? search,
      statusFilterJson: getStatusFilter(args),
    };
    return params;
  };

  const getStatusFilter = (args) => {
    let status = args && args.statusFilterJson;
    if (!status) {
      status = statusFilter;
    }

    let selectedStatuses = status
      .filter((x) => x.isChecked)
      .map((x) => x.Value);

    return status ? JSON.stringify(selectedStatuses) : "";
  };

  /**
   * Re-fetch files when the search text field is changed
   * @param event
   * @param data
   */
  const handleTextChange = (event) => {
    const searchVal = event.target.value;
    setSearch(event.target.value);

    const params = prepareGetProperties({ search: searchVal });
    debouncedGetFiles(params);
  };

  const selectFileType = (params) => {
    getFilesFromServer(params);
  };

  const retranslateFile = (selectedFiles, heapIOIsBulkAction) => {
    // retranslate each selected file
    axios
      .put("/email/retranslate", selectedFiles)
      .then((response) => {
        if (response.data.Status) {
          onSuccess(response.data.Message);

          // heap.io
          if (heapIOIsBulkAction) {
            window.heap.track("Email Manager - Bulk Retranslate");
          } else {
            window.heap.track("Email Manager - Retranslate File");
          }
        } else {
          onError("Error Retranslating", response.data.Message);
        }
        triggerRefresh();
      })
      .catch((error) => {
        console.error("Error", error);
        onError("Error Retranslating", error.response.data.Message);
      });
  };

  const debouncedGetFiles = useCallback(
    _.debounce((params) => {
      selectFileType(params);

      // heap.io
      window.heap.track("Email Manager - Use Search Bar");
    }, 200),
    [],
  );

  // Get raw file data whenever new row is selected
  useEffect(() => {
    const params = {
      isOutbound: props.isOutbound,
      fileId: fileDetails.FileId,
    };
    if (fileDetails.FileId !== "" && rows.length > 0) {
      getEmailData(params);
    }
  }, [fileDetails]);

  //Get files list whenever status filter is changed
  useEffect(() => {
    // When page first loaded (onMount), only make one backend call
    getFiles();
  }, [statusFilter, startDate, endDate]);

  useEffect(() => {
    getStatusTypes();
  }, []);

  const triggerRefresh = () => {
    getFiles(true);
    setRefreshGrid(false);
  };

  // Refreshing the endpoint grid
  useEffect(() => {
    if (refreshGrid) {
      getFiles(true);
      setRefreshGrid(false);
    }
  }, [refreshGrid]);

  const setOpenArtifactViewer = ({ isOpen }) => {
    setArtifactViewerOpen(isOpen);
  };

  // Creates alerts for connection test
  const onSuccess = (message) => {
    dispatch(
      createAlert({
        status: "Success",
        alertType: "single",
        message: message,
      }),
    );
  };

  const onError = (title, message) => {
    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title,
        message,
        buttonLabel: "Close",
      }),
    );
  };

  /**
   * Map the status number to the status label
   * @param statusNum
   * @returns
   */
  const getStatusName = (pendingStatus, fileInStatus) => {
    // error at retranslating status grouped under invalid status
    //pending staus will describe new/awaiting/confirmed/rejected
    //filein status details new/error/translated (from document to pending)
    if (fileInStatus === FileInStatusEnum.New_from_UI) {
      pendingStatus = PendingArtifactStatusEnum.New;
    } else if (fileInStatus === FileInStatusEnum.Error_at_Re_translating) {
      pendingStatus = PendingArtifactStatusEnum.ErrorInTranslation;
    }
    const matchingVal = statusFilter.find(
      (item) => item.Value === pendingStatus,
    );

    return matchingVal ? matchingVal.Label : "";
  };

  /**
   * File details and raw file data section
   * If no data available, uses default text
   * @returns Component with Connection Name, File Name, Reference, Status, File Size, Date, and File Data Text
   */
  const DocumentView = () => {
    return (
      <>
        <Box
          className="file-manager-content"
          sx={{ display: "block", width: "100%" }}
        >
          <Box className="document-section file-manager-section">
            <Box sx={{ marginBottom: "20px" }}>
              <p className="section-header">
                Connection Name: {fileDetails.ConnectionName ?? "Unknown"}
              </p>
              <p className="section-header">
                Email Subject: {fileDetails.FileName ?? "Unknown"}
              </p>
            </Box>
            <Box sx={{ marginTop: "20px" }}>
              <p className="sub-header">
                Status:{" "}
                {getStatusName(
                  fileDetails.PendingArtifactStatus,
                  fileDetails.FileInStatus,
                ) ?? "Unknown"}
              </p>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Box className="sub-header">
                  File size: {fileDetails.FileSize ?? "Unknown"} KB
                </Box>
                <Box className="receive-date">
                  Date Received:{" "}
                  {convertDate(fileDetails.ReceivedDate) ?? "Unknown"}
                </Box>
              </Box>
              <p className="sub-header">
                Confidence:{" "}
                {parseFloat(fileDetails.PendingArtifactProperties.Confidence) *
                  100}{" "}
                %
              </p>
            </Box>
          </Box>
          {!props.isOutbound &&
            fileDetails?.FileInJSONProperties?.FileInErrors?.length > 0 && (
              <>
                <Box className="document-section error-section">
                  <p className="section-header">Error Details</p>
                  <Box sx={{ marginTop: "20px" }}>
                    <List>
                      {fileDetails.FileInJSONProperties.FileInErrors.map((err, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon className="listItemIcon">
                            <span
                              style={{
                                display: "inline-block",
                                width: "9px",
                                height: "9px",
                                borderRadius: "50%",
                                backgroundColor: "red",
                              }}
                            ></span>
                          </ListItemIcon>
                          <ListItemText
                            primary={err.ErrorMessage}
                            secondary={new Date(
                              fileDetails.FileInJSONProperties.LastUpdated,
                            ).toLocaleString()}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "18px",
                              },
                              "& .MuiListItemText-secondary": {
                                fontSize: "15px",
                                color: "gray",
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              </>
            )}
          <Box className="document-section file-manager-section column">
            <div className="section-header">Raw Document Data</div>
            <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
              <p>
                {fileDataText !== "" ? fileDataText : "No file data to display"}
              </p>
            </div>
          </Box>
        </Box>
      </>
    );
  };

  const actions = [
    { label: "", onClick: () => setOpenArtifactViewer(true) },
    {
      label: "Re-translate",
      onClick: () => retranslateFile(selectionModel, true),
    },
    // placeholder, currently not available as an action outside of upload popup
    //{ label: "Cancel", onClick: () => cancelFile(selectionModel) },

    //Delete currently not supported but will be in the future
    // { label: "Delete", onClick: () => validateDelete({}) },
  ];
  const handleRefresh = () => {};

  return (
    <Box sx={{ display: "block", flexDirection: "column" }}>
      <Box style={{ display: "flex", paddingBottom: 5 }}>
        <CustomFilterDropdown schemas={schema} />
        <ShepSearchBar
          placeholder="Search"
          value={search}
          onChange={handleTextChange}
          sx={{
            width: "562px",
            paddingLeft: "10px",
            "&.MuiFormControl-root .MuiInputBase-root": {
              borderRadius: "5px",
            },
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <ShepHorizontalMenu actions={actions} />
        </div>
      </Box>
      {artifactViewerOpen && (
        <PendingArtifactEditor
          pendingArtifactId={fileDetails.PendingArtifactId}
          isOpen={artifactViewerOpen}
          setIsOpen={(isOpen) => setOpenArtifactViewer(isOpen)}
          triggerRefresh={() => setRefreshGrid(true)}
        />
      )}

      {/* Files list data grid */}
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row", marginTop: "20px" }}
      >
        <Box className="file-list-section">
          <div style={{ height: 800, flex: 1, margin: 1 }}>
            <ShepDataGridPro
              apiRef={gridRef}
              isDataLoaded={fileLoaded}
              rowHeight={120}
              secondsToStall={4}
              autoHeight={false}
              rows={rows}
              getRowId={(row) => row.PendingArtifactId}
              columns={columns}
              checkboxSelection
              disableMultipleRowSelection={true} // row is single select, checkbox is multi-select
              pagination
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 15, page: 0 },
                },
              }}
              // updateFileDetails if row or checkbox is selected, but if the row is selected, don't auto-toggle the checkbox
              onCellClick={(params, e) => {
                if (params.field !== "__check__") {
                  // stop the checkbox from being toggled whenever the row is selected
                  e.stopPropagation();
                }

                handleRowSelection(params.row);
              }}
              // logic for handling selection change. If there are multiple rows selected, selection state updated by adding or removing rows
              onRowSelectionModelChange={(newSelection) => {
                // update checkbox selection model for checkbox clicks
                setSelectionModel(newSelection);
              }}
              getRowClassName={(params) => {
                // Style class for highlighting the selected row
                if (params.id === fileDetails.FileId) {
                  return "highlight-row";
                }
                return "";
              }}
              slots={{
                baseCheckbox: ShepCheckbox,
                pagination: ShepPagination,
              }}
              sx={dataGridStyle}
            />
          </div>
        </Box>

        {rows.length !== 0 ? (
          <DocumentView />
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box className="document-section file-manager-section">
              <p className="section-header">No files available</p>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

EmailManagerContent.propTypes = {
  isOutbound: PropTypes.bool,
};

EmailManagerContent.defaultProps = {
  isOutbound: false,
};

export default EmailManagerContent;
